import moment from "moment";
import {
  LocalDecodeStarted,
  ValidateDocumentRequest,
  ValidateDocumentResponse,
} from "./ApiModels";
import { DeviceDetection } from "./DeviceDetection";
import { DocType } from "./DocType";
import { ApiErrors } from "./Errors";
import { Logger } from "./Logger";
import Request from "./Request";
import { DOC_CAPTURE_EXPERIMENT_CONTROL } from "./Experiments";
import {IdUploadStatus} from "./IdUploadStatus";
import {makePostRequest, makeGetRequest} from "../services/RequestHelpers";
import {IDVMethod} from "./IDVMethod";
import {EscapeHatchReason} from "./EscapeHatchReason";

const BASE_URL_ANON = "/u/a/api";
const V1_URL = "/v1";
const BASE_URL_ANON_V1 = `${BASE_URL_ANON}${V1_URL}`;
const BASE_URL_SECURED = "/u/s/api";
const BASE_URL_SECURED_V1 = `${BASE_URL_SECURED}${V1_URL}`;

const EXPERIMENTATION_ANON = "/experimentation/api/public/v1/open";
const EXPERIMENTATION_SECURED = "/experimentation/api/public/v1";

const VERIFF_SESSION_SECURED = `${BASE_URL_SECURED_V1}/veriff/session`;
const VERIFF_SESSION_ANON = `${BASE_URL_ANON_V1}/veriff/session`;
const VERIFF_DEVICE_TAGS_ANON = `${BASE_URL_ANON}/veriff/device-tags`;
const VERIFF_DEVICE_TAGS_SECURED = `${BASE_URL_SECURED}/veriff/device-tags`;
const VERIFF_STATUS_ANON = `${BASE_URL_ANON}/veriff/status`;
const VERIFF_STATUS_SECURED = `${BASE_URL_SECURED}/veriff/status`;
const ESCAPE_HATCH_REASON_ANON = `${BASE_URL_ANON}/escape-hatch-reason`;
const ESCAPE_HATCH_REASON_SECURED = `${BASE_URL_SECURED}/escape-hatch-reason`;
const DOC_CAP_EXPERIMENTATION_ANON = `${BASE_URL_ANON_V1}/experimentation/eventTrack`;
const DOC_CAP_EXPERIMENTATION_SECURED = `${BASE_URL_SECURED_V1}/experimentation/eventTrack`;
const EVENT_ANON = `${BASE_URL_ANON_V1}/event`;
const EVENT_SECURED = `${BASE_URL_SECURED_V1}/event`;
const FEATURE_ENABLED_ANON = `${BASE_URL_ANON_V1}/enabled-feature`;
const FEATURE_ENABLED_SECURED = `${BASE_URL_SECURED_V1}/enabled-feature`;
const ID_UPLOAD_STATUS_ANON = `${BASE_URL_ANON}/id-upload-status`;
const ID_UPLOAD_STATUS_SECURED = `${BASE_URL_SECURED}/id-upload-status`;

export const getVariation = async (uuid: string | undefined, appId: string | undefined,
                                   experimentKey: string) => {
  const experimentationUrl = !!uuid ? `${EXPERIMENTATION_ANON}/activate` : `${EXPERIMENTATION_SECURED}/activate`;
  const body = {
    globalId: !!uuid ? uuid : appId,
  };
  const response = await Request.POST(`${experimentationUrl}/${experimentKey}`, body);
  if (response.ok) {
    return response.data;
  }
  Logger.info(`Error checking variation with response: ${response.data} with status ${response.status}`);
  return DOC_CAPTURE_EXPERIMENT_CONTROL;
};

export const trackOptimizelyEvent = async (uuid: string | undefined, appId: string | undefined,
                                           eventName: string, options = {}) => {
  const experimentationUrl = !!uuid ? `${EXPERIMENTATION_ANON}/track` : `${EXPERIMENTATION_SECURED}/track`;
  const body = {
    globalId: !!uuid ? uuid : appId,
    eventName,
    attributes: {},
    eventTags: {},
    ...options,
  };
  await Request.POST(experimentationUrl, body);
};

export const getVeriffSession = async (uuid: string | undefined, appId: string | undefined,
                                       coBorrower: string | null, documentType: string | null) => {
  const url = !!uuid ? `${VERIFF_SESSION_ANON}/${uuid}`
      : `${VERIFF_SESSION_SECURED}/${appId}${documentType == null ? "" : `/${documentType}`}
      ${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;
  return await Request.POST(url, {}, {});
};

/**
 * Setting tags for device used for IDV, current status and method of IDV, and device permissions.
 * @param uuid Desktop urlUuid attached to the session
 * @param appId application ID if user is using a secure session
 * @param coBorrower if the session is associated with the coborrower
 * @param documentType SpecificDocType which user is trying to upload
 * @param method One of IDVMethod.ts
 * @param status One of IDUploadStatus.ts
 * @param permissions
 */
export const setDeviceTags = async (uuid: string | undefined, appId: string | undefined,
                                    coBorrower: string | null, documentType: string | null,
                                    method: IDVMethod | undefined, status: IdUploadStatus | undefined,
                                    permissions: Promise<MediaDeviceInfo[]>) => {
  try {
    const url = !!uuid ? `${VERIFF_DEVICE_TAGS_ANON}/${uuid}` :
        `${VERIFF_DEVICE_TAGS_SECURED}/${appId}${documentType == null ? "" : `/${documentType}`}${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;
    const deviceTags = DeviceDetection.getDeviceTags();
    const filteredPermissions = await permissions.then(
        (devices) => devices.filter((device) => device.deviceId !== ""));
    const tagsRequest = {
      "tags": deviceTags,
      "flowStatus": status,
      "verificationMethod": method,
      "cameraPermission": filteredPermissions.some((device) => device.kind === "videoinput"),
      "microphonePermission": filteredPermissions.some((device) => device.kind === "audioinput")
    };
    return await makePostRequest(url, tagsRequest, {})
    .catch((err) => {
      Logger.error(`Error setting device tags: ${err.message}`);
    });
  } catch (err) {
    Logger.error(`Error setting device tags: ${err.message}`);
  }
};

/**
 * Sets the IDV status for the given user - both on desktop (anon) and mobile (secure) by calling the backend.
 * @param uuid: urlUuid attached if the user is on Desktop.
 * @param appId: appId attached if the user is on Mobile.
 * @param coBorrower: Optional search param for coborrower.
 * @param documentType: Optional search param for document type.
 * @param idUploadStatus: one of the IdUploadStatus values.
 */
export const setIdUploadStatus = async (uuid: string | null, appId: string | null,
                                        coBorrower: string | null, documentType: string | undefined,
                                        idUploadStatus: IdUploadStatus | null) => {
  if (idUploadStatus == null) {
    return;
  }
  try {
    const url = (uuid != null) ? `${VERIFF_STATUS_ANON}` : `${VERIFF_STATUS_SECURED}`;
    const requestBody = {
      "urlUuid": uuid,
      "appId": appId,
      "status": idUploadStatus.toLowerCase(),
      "isCoborrower": coBorrower,
      "docType": !!documentType ? documentType.toLowerCase() : null
    };
    await Request.POST(url, requestBody, {});
  } catch (err) {
    Logger.error(`Error setting Upload ID status for status: ${idUploadStatus} and documentType: ${documentType}: ${err.message}`);
  }
};

/**
 * Sets the escape hatch reason for the given user - both on desktop (anon) and mobile (secure) by calling the backend.
 * @param uuid: urlUuid attached if the user is on Desktop.
 * @param appId: appId attached if the user is on Mobile.
 * @param coBorrower: Optional search param for coborrower.
 * @param documentType: Optional search param for document type.
 * @param reason: one of the Escape Hatch Reason values.
 */
export const setEscapeHatchReason = async (uuid: string | null, appId: string | null,
                                           coBorrower: string | null, documentType: string | undefined,
                                           reason: EscapeHatchReason | null) => {
  if (reason == null) {
    return;
  }
  try {
    const url = (uuid != null) ? `${ESCAPE_HATCH_REASON_ANON}` : `${ESCAPE_HATCH_REASON_SECURED}`;
    const requestBody = {
      "urlUuid": uuid,
      "appId": appId,
      "reason": reason.toLowerCase(),
      "isCoborrower": coBorrower,
      "docType": !!documentType ? documentType.toLowerCase() : null
    };
    await Request.POST(url, requestBody, {});
  } catch (err) {
    Logger.error(`Error setting escape hatch reason for status: ${reason} and documentType: ${documentType}: ${err.message}`);
  }
};

export const proxyOptimizelyEventTracking = async (uuid: string | undefined, appId: string | undefined,
                                          eventName: string, coBorrower: string | null) => {
  const url = !!uuid ? DOC_CAP_EXPERIMENTATION_ANON :
      `${DOC_CAP_EXPERIMENTATION_SECURED}${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;
  const body = {
    sessionId: uuid,
    appId,
    eventName
  };
  await Request.POST(url, body, {});
};

export const isFeatureEnabled = async (featureKey: string, uuid: string | undefined,
                                       appId: string | undefined, coBorrower: string | null) => {
  const url = !!uuid ? `${FEATURE_ENABLED_ANON}/${featureKey}/${uuid}` :
      `${FEATURE_ENABLED_SECURED}/${featureKey}/${appId}${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;
  return fetch(
      url,
      {
        credentials: "include",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        method: "GET",
      })
      .then((response) => {
        if (!response.ok) {
          return false;
        } else {
          return response.json().then ( (obj) => obj)
              .catch((ignored: Error) => false);
        }
      })
      .catch((ignored: Error) => false);
};

export const getIdUploadStatus = async (uuid: string | undefined, appId: string | undefined,
                                        coBorrower: string | null, documentType: string | null) => {
  const url = !!uuid ? `${ID_UPLOAD_STATUS_ANON}/${uuid}` :
      `${ID_UPLOAD_STATUS_SECURED}/${appId}${documentType == null ? "" : `/${documentType}`}${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;
  return makeGetRequest(url, {}).catch((err) => Logger.error(`Failed to get ID Upload status for app: ${appId},
   uuid: ${uuid}`, err));
};

export const sendBackendEvent = async (uuid: string | undefined, appId: string | undefined,
                                       eventName: string, coBorrower: string | null,
                                       boundedAttributes = {}, unboundedAttributes = {}) => {
  try {
    const url = !!uuid ? EVENT_ANON :
        `${EVENT_SECURED}${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;
    const body = {
      sessionId: uuid,
      appId,
      eventName,
      boundedAttributes,
      unboundedAttributes
    };
    await Request.POST(url, body, {});
  } catch (e) {
    Logger.error(`Failed to send backend event ${eventName}, error: ${e}`);
  }

};

export class ApiCalls {

  public static getRequiredDocs(uuid: string | undefined, appId: string | undefined, coBorrower: string | null,
                                submitMetric: boolean, documentType: string | null, useDefault: boolean,
                                handleResponse: (data: DocType[]) => void,
                                handleError: (errorMsg: string, data: any) => void) {

    // useDefault param is set to true for anon session if there is no document type passed in,
    // and we will use driver license
    const route = !!uuid ? `${BASE_URL_ANON}/v1/required/${uuid}/${submitMetric}/${useDefault}`:
     `${BASE_URL_SECURED}/v1/required/${appId}/${submitMetric}${documentType == null ? "" : `/${documentType}`}${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;

    return fetch(
      route,
      {
        credentials: "include",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        method: "GET",
      })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 403 || response.status === 401) {
            handleError(ApiErrors.LOGGED_OUT, undefined);
            return;
          }
          response.json().then((obj) => {
            if (!!obj.reason) {
              handleError(obj.reason, obj.data);
            } else {
              handleError(`status: ${obj.status}, error: ${obj.error}, message: ${obj.message}`, undefined);
            }
          })
            .catch( (error: Error) => handleError(error.message, undefined));
        } else {
          response.json().then ( (obj) => handleResponse(obj))
            .catch( (error: Error) => handleError(error.message, undefined));
        }
      })
      .catch( (error: Error) => handleError(error.message, undefined));
  }

  public static validateDocument(uuid: string | undefined, appId: string | undefined, coBorrower: string | null,
                                 resetAttempts: boolean, docType: DocType, image: string, forceUpload: boolean,
                                 localDecodeAttempts: number | undefined, decodeOffset: number | undefined,
                                 handleResponse: (data: ValidateDocumentResponse) => void,
                                 handleError: (error: string, data: any) => void, additionalTags: string[] = []) {

    const route = !!uuid ? `${BASE_URL_ANON}/v3/upload/${uuid}/${resetAttempts}` :
      `${BASE_URL_SECURED}/v3/upload/${appId}/${resetAttempts}${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;

    const document = ApiCalls.extractImage(image);
    const filename = `anon-${docType}-${moment().format("M-D-YYYY-H-m-s")}.png`;
    const deviceDetectionTags = DeviceDetection.getTags(localDecodeAttempts, "false");
    const tags = [...deviceDetectionTags, ...additionalTags];

    const body: ValidateDocumentRequest = {
      argosDocType: docType,
      decodeOffset: decodeOffset !== undefined ? decodeOffset : 0,
      document,
      filename,
      forceUpload,
      tags,
    };

    fetch(
      route,
      {
        body: JSON.stringify(body),
        credentials: "include",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        method: "POST",
      })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 403 || response.status === 401) {
            handleError(ApiErrors.LOGGED_OUT, undefined);
            return;
          }
          response.json().then((obj) => {
            if (!!obj.reason) {
              handleError(obj.reason, obj.data);
            } else {
              handleError(`status: ${obj.status}, error: ${obj.error}, message: ${obj.message}`, undefined);
            }
          })
            .catch( (error: Error) => handleError(error.message, undefined));
        } else {
          response.json().then ( (obj) => handleResponse(obj))
            .catch( (error: Error) => handleError(error.message, undefined));
        }
      })
      .catch( (error: Error) => handleError(error.message, undefined));
  }

  public static manualUploadDocument(uuid: string | undefined, appId: string | undefined, coBorrower: string | null,
                                     docType: DocType, image: string, filename: string,
                                     handleResponse: (data: ValidateDocumentResponse) => void,
                                     handleError: (error: string, data: any) => void) {

    const route = !!uuid ? `${BASE_URL_ANON}/v1/manualUpload/${uuid}` :
      `${BASE_URL_SECURED}/v1/manualUpload/${appId}/${docType.toString()}/${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;

    const document = ApiCalls.extractImage(image);
    const tags = DeviceDetection.getTags(undefined, "true");

    const body: ValidateDocumentRequest = {
      argosDocType: docType,
      decodeOffset : 0,
      document,
      filename,
      forceUpload: false,
      tags,
    };

    fetch(
      route,
      {
        body: JSON.stringify(body),
        credentials: "include",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        method: "POST",
      })
    .then((response) => {
      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          handleError(ApiErrors.LOGGED_OUT, undefined);
          return;
        }
        response.json().then((obj) => {
          if (!!obj.reason) {
            handleError(obj.reason, obj.data);
          } else {
            handleError(`status: ${obj.status}, error: ${obj.error}, message: ${obj.message}`, undefined);
          }
        })
        .catch( (error: Error) => handleError(error.message, undefined));
      } else {
        response.json().then ( (obj) => handleResponse(obj))
        .catch( (error: Error) => handleError(error.message, undefined));
      }
    })
    .catch( (error: Error) => handleError(error.message, undefined));
  }

  public static localDecodeStarted(uuid: string | undefined, appId: string | undefined, docType: DocType,
                                   coBorrower: string | null) {

    const route = !!uuid ? `${BASE_URL_ANON}/v1/localDecode/${uuid}/${docType}` :
      `${BASE_URL_SECURED}/v1/localDecode/${appId}/${docType}${coBorrower == null ? "" : `?coborrower=${coBorrower}`}`;

    const tags = DeviceDetection.getTags(1, undefined);

    const body: LocalDecodeStarted = {
      docType,
      tags,
    };

    const handleError = (error: any) => {
      Logger.error(`Local decode started call failed: ${error}`);
    };

    fetch(
      route,
      {
        body: JSON.stringify(body),
        credentials: "include",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        method: "POST",
      })
    .then((response) => {
      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          handleError(ApiErrors.LOGGED_OUT);
          return;
        }
        response.json().then((obj) => {
          if (!!obj.reason) {
            handleError(obj.reason);
          } else {
            handleError(`status: ${obj.status}, error: ${obj.error}, message: ${obj.message}`);
          }
        })
        .catch( (error: Error) => handleError(error.message));
      } else {
        // void response
      }
    })
    .catch( (error: Error) => handleError(error.message));
  }

  public static shouldUseVeriff(anon: boolean, id: string, coBorrower: string | null,
                                handleResponse: (shouldUseVeriff: boolean) => void) {
    const urlBase = anon ? BASE_URL_ANON_V1 : BASE_URL_SECURED_V1;
    fetch(
        `${urlBase}/shouldUseVeriff/${id}${coBorrower == null? "" : `?coborrower=${coBorrower}`}`,
        {
          credentials: "include",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
          method: "GET",
        })
        .then((response) => {
          if (!response.ok) {
            handleResponse(false);
          } else {
            response.json().then ( (obj) => handleResponse(obj))
                .catch((ignored: Error) => handleResponse(false));
          }
        })
        .catch((ignored: Error) => handleResponse(false));
  }

  public static getIsLoggedIn(handleResponse: (isLoggedIn: boolean) => void) {
    fetch(
      `${BASE_URL_SECURED}/v1/isLoggedIn`,
      {
        credentials: "include",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        method: "GET",
      })
      .then((response) => {
        if (!response.ok) {
          handleResponse(false);
        } else {
          response.json().then ( (obj) => handleResponse(obj))
            .catch((ignored: Error) => handleResponse(false));
        }
      })
      .catch((ignored: Error) => handleResponse(false));
  }

  public static extractImage = (image: string): string => {
    // remove file info at beginning of string
    return image.split(",")[1];
  }
}



// WEBPACK FOOTER //
// ./src/constants/ApiCalls.ts